import { useMemo, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  Badge,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  ConfirmDialog,
  DataTable,
  Toggle,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { PLAN_FEATURE_KEYS, PLAN_LIMITS, isPlanFeatureKey } from '@bbpos/types';
import { formatCurrency } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { subscriptionService } from '@/services/subscription.service';
import type { SubscriptionPlan } from '@/types/subscription';
import { PlanCard } from '@/components/features/subscriptions/PlanCard';
import { PlanEditorModal } from '@/components/features/subscriptions/PlanEditorModal';
import {
  GLASS_PANEL,
  cn,
  planTone,
  toneClasses,
} from '@/components/features/subscriptions/design-tokens';
import { formatLimit } from '@/components/features/subscriptions/plan-entitlements';
import { useAuthStore } from '@/stores/use-auth-store';

const ROLE_LEVELS: Record<string, number> = {
  SUPER_ADMIN: 100,
  ADMIN: 80,
  MODERATOR: 60,
  OWNER: 50,
  MANAGER: 30,
  CASHIER: 5,
};

function Icon({ d }: { d: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

function PlansContent() {
  const currency = useTenantCurrency();
  const profile = useAuthStore((s) => s.profile);
  const level = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const isSuperAdmin = level >= 100;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly');
  const [showInactive, setShowInactive] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [deactivatePlan, setDeactivatePlan] = useState<SubscriptionPlan | null>(null);

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ['subscription-plans', showInactive],
    queryFn: () => subscriptionService.listPlans(showInactive && isSuperAdmin),
  });

  const sortedPlans = useMemo(
    () => [...plans].sort((a, b) => a.sort_order - b.sort_order),
    [plans],
  );
  const deactivateMutation = useMutation({
    mutationFn: (id: string) => subscriptionService.deactivatePlan(id),
    onSuccess: () => {
      toast({ title: 'Plan deactivated', variant: 'success' });
      setDeactivatePlan(null);
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
    },
    onError: (err: Error) => {
      toast({ title: err.message, variant: 'error' });
      setDeactivatePlan(null);
    },
  });

  const columns: ColumnDef<SubscriptionPlan>[] = [
    {
      key: 'name',
      header: 'Slug',
      render: (_v, row) => {
        const palette = toneClasses(planTone(row.name));
        return (
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wider',
              palette.pill,
            )}
          >
            <span className={cn('h-1.5 w-1.5 rounded-full', palette.dot)} />
            {row.name}
          </span>
        );
      },
    },
    {
      key: 'display_name',
      header: 'Display name',
      render: (_v, row) => (
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
            {row.display_name}
            {row.badge && (
              <Badge variant="warning" size="sm">
                {row.badge}
              </Badge>
            )}
          </p>
          {row.description && (
            <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400 truncate max-w-[280px]">
              {row.description}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'price_monthly',
      header: 'Monthly',
      render: (_v, row) => (
        <span className="text-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
          {formatCurrency(row.price_monthly, currency)}
        </span>
      ),
    },
    {
      key: 'price_yearly',
      header: 'Yearly',
      render: (_v, row) => (
        <span className="text-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
          {formatCurrency(row.price_yearly, currency)}
        </span>
      ),
    },
    {
      key: 'max_locations',
      header: 'Limits',
      render: (_v, row) => (
        <dl className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5 text-xs">
          {PLAN_LIMITS.map((l) => (
            <div key={l.column} className="contents">
              <dt className="text-neutral-500 dark:text-neutral-400">{l.label}</dt>
              <dd className="text-right font-medium tabular-nums text-neutral-800 dark:text-neutral-200">
                {formatLimit(l, row[l.column] ?? null)}
              </dd>
            </div>
          ))}
        </dl>
      ),
    },
    {
      key: 'features',
      header: 'Features',
      render: (_v, row) => (
        <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium tabular-nums text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
          {row.features?.filter(isPlanFeatureKey).length ?? 0} of {PLAN_FEATURE_KEYS.length}
        </span>
      ),
    },
    {
      key: 'is_active',
      header: 'Status',
      render: (_v, row) => (
        <div className="flex flex-wrap items-center gap-1">
          <Badge variant={row.is_active ? 'success' : 'neutral'} size="sm">
            {row.is_active ? 'Active' : 'Inactive'}
          </Badge>
          {row.show_on_website && (
            <Badge variant="brand" size="sm">
              On website
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (_v, row) =>
        isSuperAdmin ? (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditingPlan(row);
                setEditorOpen(true);
              }}
            >
              Edit
            </Button>
            {row.is_active && (
              <Button variant="ghost" size="sm" onClick={() => setDeactivatePlan(row)}>
                Deactivate
              </Button>
            )}
          </div>
        ) : (
          <span className="text-xs text-neutral-400 dark:text-neutral-500">—</span>
        ),
    },
  ];

  return (
    <>
      {/* Hero */}
      <div className="relative mb-6 overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-700 p-6 text-white shadow-[0_20px_60px_-20px_rgba(5,150,105,0.55)] animate-fade-in">
        <div className="pointer-events-none absolute -right-20 -top-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider ring-1 ring-inset ring-white/20">
              <Icon d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              Plan catalog
            </div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-white">Subscription plans</h1>
              {!isSuperAdmin && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset ring-white/30">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-3 w-3">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View only
                </span>
              )}
            </div>
            <p className="mt-1 text-emerald-50">
              {isSuperAdmin
                ? 'Define the tiers offered to every tenant on the platform. Pricing changes here ripple into invoices.'
                : 'Review the plans offered to every tenant. Only Super Admins can create, edit, or deactivate plans.'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/20 p-1 text-xs font-medium ring-1 ring-inset ring-white/20">
              {(['monthly', 'yearly'] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  className={cn(
                    'rounded-full px-3 py-1.5 capitalize transition',
                    billingCycle === k
                      ? 'bg-white text-emerald-700 shadow'
                      : 'text-white/90 hover:text-white',
                  )}
                  onClick={() => setBillingCycle(k)}
                >
                  {k}
                </button>
              ))}
            </div>
            {isSuperAdmin && (
              <Button
                variant="secondary"
                onClick={() => {
                  setEditingPlan(null);
                  setEditorOpen(true);
                }}
                className="border-white/30 bg-white/20 text-white hover:bg-white/30"
              >
                + New plan
              </Button>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={320} className="rounded-2xl" />
          ))}
        </div>
      ) : sortedPlans.length === 0 ? (
        <div className={cn(GLASS_PANEL, 'p-10 mb-6')}>
          <EmptyState
            variant="section"
            title="No active plans"
            description="Create a plan to start accepting paid tenants."
            action={
              isSuperAdmin
                ? {
                    label: 'Create plan',
                    onClick: () => {
                      setEditingPlan(null);
                      setEditorOpen(true);
                    },
                  }
                : undefined
            }
          />
        </div>
      ) : (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {sortedPlans.map((p) => (
            <PlanCard
              key={p.id}
              plan={p}
              billingCycle={billingCycle}
              currency="INR"
              canEdit={isSuperAdmin}
              showWebsiteStatus
              onEdit={(plan) => {
                setEditingPlan(plan);
                setEditorOpen(true);
              }}
            />
          ))}
        </div>
      )}

      {/* Table + inactive toggle */}
      <div className={cn(GLASS_PANEL, 'p-5')}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-base font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
              All plans
            </h2>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Canonical list used by tenant subscription flows and the pricing page.
            </p>
          </div>
          {isSuperAdmin && (
            <label className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
              <Toggle checked={showInactive} onChange={setShowInactive} />
              Show inactive
            </label>
          )}
        </div>
        <DataTable columns={columns} data={sortedPlans} variant="default" />
      </div>

      <PlanEditorModal
        open={editorOpen}
        plan={editingPlan}
        onClose={() => setEditorOpen(false)}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ['subscription-plans'] })}
      />

      {deactivatePlan && (
        <ConfirmDialog
          open={Boolean(deactivatePlan)}
          title={`Deactivate ${deactivatePlan.display_name}?`}
          description={
            deactivatePlan.active_tenant_count && deactivatePlan.active_tenant_count > 0
              ? `${deactivatePlan.active_tenant_count} tenant(s) are still on this plan. Deactivation will fail while any tenant is active.`
              : 'Existing subscribers keep their plan; new sign-ups cannot choose it.'
          }
          confirmLabel="Deactivate"
          variant="warning"
          loading={deactivateMutation.isPending}
          onConfirm={() => deactivateMutation.mutate(deactivatePlan.id)}
          onCancel={() => setDeactivatePlan(null)}
        />
      )}
    </>
  );
}

export function SubscriptionPlansPage() {
  return (
    <ErrorBoundary>
      <PageContainer variant="fluid">
        <PlansContent />
      </PageContainer>
    </ErrorBoundary>
  );
}
